/* Web Push subscription helper for SİNE81.
 *
 * Talks to /api/push/* on the backend. The Service Worker (public/sw.js)
 * receives the actual notifications.
 */
import api from "./api";

const SW_PATH = "/sw.js";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) output[i] = rawData.charCodeAt(i);
  return output;
}

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function currentPermission() {
  if (!isPushSupported()) return "unsupported";
  return Notification.permission; // "default" | "granted" | "denied"
}

export async function registerServiceWorker() {
  if (!isPushSupported()) return null;
  try {
    // `updateViaCache: 'none'` tells the browser to re-fetch sw.js on
    // every registration (not reuse a 24h-cached copy). Combined with
    // `reg.update()` below this guarantees the newest SW ships even on
    // devices that have an older one installed.
    const reg = await navigator.serviceWorker.register(SW_PATH, {
      scope: "/",
      updateViaCache: "none",
    });
    try { await reg.update(); } catch (_) { /* best-effort */ }
    await navigator.serviceWorker.ready;
    return reg;
  } catch (e) {
    console.error("[push] SW registration failed", e);
    return null;
  }
}

export async function getCurrentSubscription() {
  if (!isPushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

/**
 * Ask the user to enable notifications. Returns the subscription object
 * (already persisted on the backend) or throws on failure.
 */
export async function subscribeToPush({ provinceId } = {}) {
  if (!isPushSupported()) throw new Error("Tarayıcınız push bildirimleri desteklemiyor");

  const reg = (await registerServiceWorker()) || (await navigator.serviceWorker.ready);
  if (!reg) throw new Error("Service worker kaydedilemedi");

  // Ask the user
  let permission = Notification.permission;
  if (permission === "default") {
    permission = await Notification.requestPermission();
  }
  if (permission !== "granted") {
    throw new Error("Bildirim izni reddedildi");
  }

  // Get VAPID public key from backend
  const { data: keyData } = await api.get("/push/vapid-public-key");
  const applicationServerKey = urlBase64ToUint8Array(keyData.public_key);

  // Reuse existing subscription if any, else create
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });
  }

  // Persist to backend
  const json = sub.toJSON();
  await api.post("/push/subscribe", {
    endpoint: json.endpoint,
    keys: json.keys,
    province_id: provinceId || null,
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
  });
  return sub;
}

export async function unsubscribeFromPush() {
  const sub = await getCurrentSubscription();
  if (!sub) return false;
  try {
    await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
  } catch (_e) {
    /* network failure shouldn't block local unsubscribe */
  }
  try {
    await sub.unsubscribe();
  } catch (_e) {
    /* browser-side unsubscribe is best-effort */
  }
  return true;
}

export async function updateProvinceForCurrent(provinceId) {
  const sub = await getCurrentSubscription();
  if (!sub) return false;
  const json = sub.toJSON();
  await api.post("/push/subscribe", {
    endpoint: json.endpoint,
    keys: json.keys,
    province_id: provinceId || null,
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
  });
  return true;
}
